import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { CONDITION_MEET_TEXT, CONDITION_STATUS_TEXT } from './lang';
import { PAGE_TEXT } from '../lang';
import type { APPLICATION_MODULE } from '../../../ApplicationForm';
import {
  useGetApplicantConditionMeet,
  useSubmitConditionMeetRequest,
} from '../../../../graphql';
import { type Condition } from '../../../../../Assessment/graphql';
import {
  conditionCheckTypeOptions,
  AssessmentAccordion,
  type CONDITION_TYPE,
} from '../../../../../Assessment';
import {
  Alert,
  type AlertDetails,
  BoxPanel,
  Button,
  Dialog,
  Loading,
} from '../../../../../../components';
import {
  getSubmissionNotes,
  shouldApplicantUpdate,
} from './meetConditionFunctions';
import { ConditionMeetItem } from './ConditionMeetItem';

const {
  MEETING_CONDITION_STATUS,
  MEETING_CONDITION_SUBMIT_APPLICATION,
  MEETING_CONDITION_UPDATED_DATE,
  MEET_CONDITION_DEADLINE,
  CONTACT_PERSON_NAME,
  CONTACT_PERSON_EMAIL,
  SUBMIT_CONDITION_CONFIRM_TITLE,
  SUBMIT_CONDITION_CONFIRM_CONTENT,
  SUBMIT,
  CANCEL,
} = PAGE_TEXT;

export type ConditionMeetProps = {
  refetchApplication: () => void;
  handleChangeApplicationActivated: (activatedId: APPLICATION_MODULE) => void;
};

export const ConditionMeet: React.FC<ConditionMeetProps> = (
  props: ConditionMeetProps,
) => {
  const { handleChangeApplicationActivated, refetchApplication } = props;

  const { applicationId } = useParams();

  const [openAlert, setOpenAlert] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const { conditionMeet, loading, error, refetch } =
    useGetApplicantConditionMeet(applicationId as string);

  const {
    conditions,
    status,
    updatedAt,
    conditionFulfillingDeadline,
    contactDepartmentPersonName,
    contactDepartmentPersonEmail,
  } = conditionMeet;

  const submitConditionMeetRequest = useSubmitConditionMeetRequest();
  const handleSubmission = async (): Promise<void> => {
    try {
      await submitConditionMeetRequest({
        variables: {
          input: {
            applicationId,
            conditions,
          },
        },
      });
      setAlertDetails({
        alertContent: 'Meet Condition Application Submitted Successfully. ',
        alertType: 'success',
      });
      refetch();
      refetchApplication();
    } catch (e: any) {
      setAlertDetails({
        alertContent: e,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const openSubmitDialog = (): void => {
    setSubmitDialogOpen(true);
  };

  const cancelSubmitApplication = (): void => {
    setSubmitDialogOpen(false);
  };

  const statusDisplay = [
    {
      label: MEETING_CONDITION_STATUS,
      value:
        CONDITION_STATUS_TEXT[status as keyof typeof CONDITION_STATUS_TEXT] ??
        status,
    },
    {
      label: MEETING_CONDITION_UPDATED_DATE,
      value: updatedAt,
    },
    {
      label: MEET_CONDITION_DEADLINE,
      value: conditionFulfillingDeadline,
    },
    {
      label: CONTACT_PERSON_NAME,
      value: contactDepartmentPersonName,
    },
    {
      label: CONTACT_PERSON_EMAIL,
      value: contactDepartmentPersonEmail,
    },
  ];

  const needConditionMeet = conditions.length > 0;
  const submissionNotes = getSubmissionNotes(status);
  const disableSubmission = !shouldApplicantUpdate(status);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return loading ? (
    <Loading />
  ) : (
    <Container>
      {needConditionMeet ? (
        <>
          <Dialog
            openDialog={submitDialogOpen}
            dialogTitle={SUBMIT_CONDITION_CONFIRM_TITLE}
            dialogAction={SUBMIT}
            cancelAction={CANCEL}
            maxWidth={'sm'}
            onDialogAction={handleSubmission}
            onDialogClose={cancelSubmitApplication}
          >
            <Typography margin={'0rem 1.5rem 2rem'}>
              {SUBMIT_CONDITION_CONFIRM_CONTENT}
            </Typography>
          </Dialog>
          <Alert
            openAlert={openAlert}
            alertDetails={alertDetails}
            setOpenAlert={setOpenAlert}
          />
          <div className="mb-6 px-6">
            {CONDITION_MEET_TEXT.SUBMIT_STEPS.map((item, index) => {
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: item,
                  }}
                />
              );
            })}
          </div>
          <div className="px-6">
            {statusDisplay.map((item, index) => {
              const { label, value } = item;
              return (
                <div key={index} className="mb-4">
                  <span className="text-[#224295]">{label}: </span>
                  <span>{value}</span>
                </div>
              );
            })}
          </div>
          <div>
            {conditions.map((condition: Condition, index) => {
              const { item, passed, remark, title, detail } = condition ?? {};
              const itemLabel =
                conditionCheckTypeOptions.find(option => option.value === item)
                  ?.label ?? PAGE_TEXT.OTHER_TEST_REQUIREMENT;

              return (
                <AssessmentAccordion
                  key={index}
                  title={itemLabel as string}
                  defaultExpanded
                  AssessDetails={
                    <ConditionMeetItem
                      item={item as CONDITION_TYPE}
                      title={title}
                      detail={detail}
                      passed={passed}
                      remark={remark}
                      status={status}
                      handleChangeApplicationActivated={
                        handleChangeApplicationActivated
                      }
                    />
                  }
                />
              );
            })}
          </div>
          <div className="my-8 text-center">
            <div className="text-[#951d1d] mb-0.5 text-base">
              {submissionNotes}
            </div>
            <Button
              variant="contained"
              disabled={disableSubmission}
              onClick={openSubmitDialog}
            >
              {MEETING_CONDITION_SUBMIT_APPLICATION}
            </Button>
          </div>
        </>
      ) : (
        <BoxPanel
          title={PAGE_TEXT.NO_ACTION_REQUIRED}
          description={PAGE_TEXT.YOU_DO_NOT_NEED_TO_MEET_ANY_CONDITION}
          icon="succeed"
          sx={{
            backgroundColor: 'lightgrey',
            borderRadius: '8px',
            padding: '1rem',
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
  }

  .MuiBox-root {
    padding: 0 0 0.1rem 0.5rem;
  }

  .MuiGrid-container {
    padding: 0.5rem 1rem 0 2rem !important;
  }
`;
